import * as styleVars from '@common/styles/constants'
import * as yup from 'yup'

import {
  AuthFormHeader,
  AuthFormSupportingText,
  AuthInnerFormWrapper,
  authFormSubmitBtnCss,
} from '../styles'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import Button from '@common/components/button/v2'
import { Form } from '@common/components/form'
import Link from 'next/link'
import React from 'react'
// src
import Spinner from '@common/components/spinner'
import { css } from '@emotion/react'
import { passswordRestoreInit } from '@api/index'
import { useApolloClient } from '@apollo/client'
import { useIntl } from '@palqee/intl'
import { useRouter } from 'next/router'
import { yupResolver } from '@hookform/resolvers/yup'

interface FormInterface {
  email: string
}

const PasswordRecoverInit: React.FC<React.PropsWithChildren<{}>> = () => {
  const intl = useIntl()
  const client = useApolloClient()
  const router = useRouter()
  const [passwordRecoverLoading, setPasswordRecoverLoading] =
    React.useState(false)

  const PasswordRecoverInitValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email(
        intl.formatMessage({
          id: 'EMAIL_INVALID',
          defaultMessage: 'Email address is invalid',
        }),
      )
      .required(
        intl.formatMessage({
          id: 'EMAIL_REQUIRED',
          defaultMessage: 'Email address is required',
        }),
      ),
  })

  const methods = useForm<FormInterface>({
    mode: 'onChange',
    resolver: yupResolver(PasswordRecoverInitValidationSchema),
    defaultValues: {
      email: '',
    },
  })
  const {
    formState,
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = methods

  const onSubmitRecoverAccountForm = async (formData: FormInterface) => {
    setPasswordRecoverLoading(true)
    try {
      console.log('placeholder')

      await passswordRestoreInit(client, {
        username: formData.email,
      })

      await router.push('/auth/forgot-password/verify-email/')
      setPasswordRecoverLoading(false)
    } catch (err) {
      console.log('err *********', err)
      setPasswordRecoverLoading(false)

      if (err?.message?.includes('user not found')) {
        return setError('email', {
          type: 'emailError',
          message: intl.formatMessage({
            id: 'USER_NOT_FOUND',
            defaultMessage: 'User not found',
          }),
        })
      }
      setError('email', {
        type: 'emailError',
        message: intl.formatMessage({
          id: 'UNEXPECTED_ERROR',
          defaultMessage: 'Unexpected error. Refresh the page and try again',
        }),
      })
    }
  }

  const renderFormSubmitButtonText = (): string | JSX.Element => {
    if (!passwordRecoverLoading)
      return (
        <div>
          {intl.formatMessage({
            id: 'FORGOT_PASSWORD_INIT_BUTTON',
            defaultMessage: 'Send Instructions',
          })}
        </div>
      )

    return (
      <>
        <Spinner className="pq-mr-1" color={styleVars.white} />
        {intl.formatMessage({
          id: 'FORGOT_PASSWORD_INIT_BUTTON',
          defaultMessage: 'Send Instructions',
        })}
      </>
    )
  }

  return (
    <AuthInnerFormWrapper className="LoginForm">
      <AuthFormHeader data-cy="forgot-password">
        {intl.formatMessage({
          id: 'FORGOT_PASSWORD_INIT_TITLE',
          defaultMessage: 'Reset Password',
        })}
      </AuthFormHeader>
      <AuthFormSupportingText>
        {intl.formatMessage({
          id: 'FORGOT_PASSWORD_INIT_DESC',
          defaultMessage: "We'll send you a link to reset your password",
        })}
      </AuthFormSupportingText>
      <div className="login-form-wrapper">
        <Form
          data-cy="submit"
          onSubmit={handleSubmit(onSubmitRecoverAccountForm as any)}
        >
          <FormProvider {...methods}>
            <div data-cy="form-forgotpw-email-input">
              <Controller
                render={({ field }) => (
                  <Form.Input
                    {...field}
                    caption={intl.formatMessage({
                      id: 'EMAIL',
                      defaultMessage: 'Email',
                    })}
                    errorMessage={
                      formState.isSubmitted && errors?.email?.message
                    }
                    errorBoard
                    type="email"
                  />
                )}
                name="email"
                control={control}
              />
            </div>

            <Button
              btnTheme="primary-red"
              type="submit"
              id="send-instructions-btn"
              wrapperCss={authFormSubmitBtnCss}
              css={authFormSubmitBtnCss}
              disabled={!formState.isValid || passwordRecoverLoading}
            >
              {renderFormSubmitButtonText()}
            </Button>

            <div
              className="pq-mt-2"
              css={css`
                text-size: 12px;
                font-weight: 700;
              `}
            >
              <p className="pq-text-center pq-font-weight-bold">
                {intl.formatMessage({
                  id: 'BACK_TO',
                  defaultMessage: 'Back to',
                })}{' '}
                <span
                  className="pq-text-color-primary-red"
                  data-cy="forgot-password-back"
                >
                  <Link href="/auth/login">
                    {intl.formatMessage({
                      id: 'LOGIN',
                      defaultMessage: 'Login',
                    })}
                  </Link>
                </span>
              </p>
            </div>
          </FormProvider>
        </Form>
      </div>
    </AuthInnerFormWrapper>
  )
}

export default PasswordRecoverInit
